import { ResponsiveLine, type LineSeries } from '@nivo/line';
import { useId } from 'react';
import { ChartFrame, type ChartCardProps } from '~/components/dashboard/charts/ChartCard';
import {
  CHART_AXIS,
  CHART_MARGIN,
  CHART_SERIES_TOKENS,
} from '~/components/dashboard/charts/chartConfig';
import { ChartLegend } from '~/components/dashboard/charts/ChartLegend';
import { useChartTheme } from '~/components/dashboard/charts/useChartTheme';
import { thinTicks, wholeNumberTicks } from '~/lib/chartTicks';
import type { TokenName } from '~/lib/theme-colors';

export interface LineChartCardProps extends ChartCardProps {
  /** One line per series; every series shares the same x values. */
  series: LineSeries[];
  /** Colours by series order; defaults to the shared series tokens. */
  seriesTokens?: TokenName[];
  /** Whole-number axis and tooltip (counts), instead of up to one decimal. */
  integers?: boolean;
  /** d3 format for the tooltip and y axis, e.g. `$,.2f`. Overrides `integers`. */
  valueFormat?: string;
}

/** A time series as a straight 2px line over a gradient; a legend only for more than one series (DESIGN.md §8.1). */
export function LineChartCard({
  series,
  seriesTokens = CHART_SERIES_TOKENS,
  integers,
  valueFormat,
  ...card
}: LineChartCardProps) {
  const { nivoTheme, color } = useChartTheme();
  const gradientId = useId().replace(/:/g, '');
  const colors = series.map((_, index) => color(seriesTokens[index % seriesTokens.length]));
  const format = valueFormat ?? (integers ? ' >-,d' : ' >-,.1~f');
  const yMax = Math.max(0, ...series.flatMap((s) => s.data.map((point) => Number(point.y) || 0)));
  const yTicks = integers ? wholeNumberTicks(yMax) : undefined;
  const xTicks = thinTicks((series[0]?.data ?? []).map((point) => String(point.x)));

  return (
    <ChartFrame
      {...card}
      isEmpty={!series.some((s) => s.data.some((point) => Number(point.y) > 0))}
      legend={
        series.length > 1 && (
          <ChartLegend
            items={series.map((s, i) => ({
              id: String(s.id),
              label: String(s.id),
              color: colors[i],
            }))}
          />
        )
      }
    >
      <ResponsiveLine
        data={series}
        theme={nivoTheme}
        colors={colors}
        margin={CHART_MARGIN}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false }}
        yFormat={format}
        curve="linear"
        lineWidth={2}
        enableArea
        areaOpacity={1}
        defs={colors.map((c, index) => ({
          id: `${gradientId}-${index}`,
          type: 'linearGradient',
          colors: [
            { offset: 0, color: c, opacity: 0.4 },
            { offset: 100, color: c, opacity: 0.1 },
          ],
        }))}
        fill={series.map((s, index) => ({ match: { id: s.id }, id: `${gradientId}-${index}` }))}
        gridXValues={xTicks}
        gridYValues={yTicks}
        axisTop={null}
        axisRight={null}
        axisBottom={{ ...CHART_AXIS, tickValues: xTicks }}
        axisLeft={{
          ...CHART_AXIS,
          format: integers || valueFormat ? format : undefined,
          tickValues: yTicks,
        }}
        pointSize={6}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        enableTouchCrosshair
        useMesh
      />
    </ChartFrame>
  );
}
